import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import StylesContext from '../context/StylesContext';
import MainContainer, { GlobalStyles } from '../components/MainContainer';
import CategoriesCard from '../components/CategoriesCard';
import StyledContainer from '../components/StyledContainer';
import StyledTitle from '../components/StyledTitle';
import MainLogoContainer from '../components/MainLogoContainer';
import MenuIcon from '../components/MenuIcon';
import TopText from '../components/TopText';
import Footer from '../components/Footer';
import BackButton from '../components/Buttons/BackButton';
import subcategoriesProps from '../interfaces/SubcategoryProps';
import useSessionStorage from '../hooks/useSessionStorage';
import { useLanguage } from '../hooks/languageService';
import { categoriesPath, categoryItemsPath, subcategoryItemsPath } from '../helpers/menuRoutes';
import '../styles/CategoryList.css';

const AllItemsTranslate = {
  'pt-BR': 'Ver todos os itens',
  'en-US': 'See all items',
  'es-ES': 'Ver todos los artículos'
};

const SubcategoriesList = () => {
  const { styles, setStyles } = useContext(StylesContext);
  const [subcategories, setSubcategories] = useState<Array<subcategoriesProps>>();
  const { getStylesFromSessionStorage } = useSessionStorage();

  const { menu, category } = useParams<{ menu: string, category: string }>();
  const history = useNavigate();
  const { selectedLanguage } = useLanguage();
  const langId = selectedLanguage.split('-')[0];
  const langEndpoint = langId.includes('pt') ? '' : `-${langId}`;

  useEffect(() => {
    const getStyles = async () => {
      try {
        const stylesSessionStorage = getStylesFromSessionStorage();
        if (stylesSessionStorage) {
          setStyles(stylesSessionStorage);
        } else {
          const { data } = await api.get(`menu_styles/${menu}`);
          setStyles(data);
        };
      } catch (error: any) {
        console.error(error.message);
      };
    };
    getStyles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu]);

  useEffect(() => {
    const getSubcategories = async () => {
      try {
        const { data } = await api.get(
          `categories/subcategories/?url_id=${menu}${langEndpoint}&category_name=${encodeURIComponent(category || '')}`
        );
        if (!data?.length) {
          // categoria sem subcategorias cadastradas: segue direto para os itens
          history(categoryItemsPath(menu, category), { replace: true });
          return;
        };
        setSubcategories(data);
      } catch (error: any) {
        console.error(error.message);
        history(categoryItemsPath(menu, category), { replace: true });
      };
    };
    getSubcategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, category, langEndpoint]);

  return (
    <MainContainer $backgroundColor={styles.bgColor} className='categories_list_page'>
      <GlobalStyles $backgroundColor={styles?.bgColor} />
      <StyledContainer className='logo_client_categories_wrapper'>
        <TopText />
        <MainLogoContainer wrapperClass='logo_client_categories' />
      </StyledContainer>
      <StyledContainer className='container_title_icon'>
        <BackButton
          color={styles.menuIconColor}
          className='subcategories_back_btn'
          onClickFunc={() => history(categoriesPath(menu))}
        />
        <StyledContainer className='menu_icon_wrapper'>
          <MenuIcon color={styles.menuIconColor} />
        </StyledContainer>
        <StyledTitle
          className='title_categories_list'
          $textColor={styles.textColor}
        >
          {category?.toUpperCase()}
        </StyledTitle>
      </StyledContainer>
      <StyledContainer className='categories_card_container'>
        {
          subcategories?.map(subcategoryEl => (
            <CategoriesCard
              isButton={true}
              key={subcategoryEl.id ?? subcategoryEl.subcategory_name}
              categorieElement={subcategoryEl.subcategory_name}
              categorieImage={subcategoryEl.subcategory_image || subcategoryEl.subcategory_icon}
              to={subcategoryItemsPath(menu, category, subcategoryEl.subcategory_name)}
            />
          ))
        }
        {
          // uma categoria pode ter subcategorias e itens soltos ao mesmo tempo;
          // este atalho garante que os itens soltos continuem acessíveis
          subcategories?.length ? (
            <CategoriesCard
              isButton={true}
              categorieElement={AllItemsTranslate[selectedLanguage as keyof typeof AllItemsTranslate]}
              to={categoryItemsPath(menu, category)}
            />
          ) : null
        }
      </StyledContainer>
      <Footer $footerLogoColor={styles.footerColor} classCategories='footer-categories' />
    </MainContainer>
  );
};

export default SubcategoriesList;
